import { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { encode } from 'html-entities';
import debounce from 'lodash.debounce';

import { errorLogger, fetchJSON, scroll, uri } from 'utils';
import classnames from 'consts/classnames';

import { isSearchValueValid } from '../utils';
import {
    HEADER_SEARCH_QUERY_PARAMETER_ID,
    HEADER_SEARCH_CATEGORY_PARAMETER_ID
} from '../constants';

let searchRequestAbortController = null;

/**
 * @typedef Output
 * @type {Object}
 * @property {Function} handleFocus
 * @property {Function} handleBlur
 * @property {Function} handleSubmit
 * @property {Function} handleChange
 * @property {Function} handleSelectChange
 * @property {Function} handleCloseButtonClick
 * @property {Function} handleSearchButtonClick
 * @property {Function} handleCloseSearchButtonClick
 * @property {Function} handleClickOutside
 * @property {boolean} isFocused
 * @property {string} categoryValue
 * @property {string} searchValue
 * @property {any} suggestions
 * @property {string} composedSerpUrl
 * @property {boolean} menuIsOpen
 * @property {MutableRefObject} inputRef
 * @property {number} resultsAmount
 * @property {boolean} isLoading
 */

/**
 * @typedef  Params
 * @type {Object}
 * @property {string} api
 * @property {string} serpUrl
 * @property {string} siteName
 * @property {string} culture
 * @property {string} searchInitialValue
 * @property {string} categoryInitialValue
 * @property {boolean} menuIsNotEmpty
 * @property {Object} suggestionsSettings
 */

/**
 * @param {Params}
 * @returns {Output}
 */

const useSearch = ({
    api,
    serpUrl,
    siteName,
    culture,
    categoryInitialValue,
    searchInitialValue,
    menuIsNotEmpty,
    suggestionsSettings
}) => {
    const [searchValue, setSearchValue] = useState(searchInitialValue);
    const [categoryValue, setCategoryValue] = useState(categoryInitialValue);
    const [isFocused, setIsFocused] = useState(false);
    const [menuIsOpen, setMenuIsOpen] = useState(false);
    const [suggestions, setSuggestions] = useState(null);
    const [mobileSearchIsOpen, setMobileSearchIsOpen] = useState(false);
    const [resultsAmount, setResultsAmount] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const shouldShowSuggestions = searchValue && suggestions;

    const { maxLength, minLength } = suggestionsSettings;

    const bodyRef = useRef(null);
    const inputRef = useRef(null);

    // SERP URL redirect
    const composedSerpUrl = useMemo(() => {
        const escapedValue = encode(searchValue);
        const formattedSerpURL = uri.format({
            pathname: serpUrl,
            query: {
                [HEADER_SEARCH_QUERY_PARAMETER_ID]: escapedValue,
                [HEADER_SEARCH_CATEGORY_PARAMETER_ID]: categoryValue
            }
        });

        return formattedSerpURL;
    }, [searchValue, categoryValue]);

    const clearSuggestions = () => {
        setIsLoading(false);

        // cancel previous request
        if (searchRequestAbortController) {
            searchRequestAbortController.abort();
        }

        setSuggestions(null);
    };

    const handleSearchSuccess = response => {
        if (!response || !response.payload) {
            return;
        }

        setSuggestions(response.payload.data);
        setResultsAmount(response.payload.resultsAmount);

        if (menuIsNotEmpty && menuIsOpen) {
            setMenuIsOpen(false);
        }
    };

    const handleSearchError = error => {
        errorLogger(error);

        setSuggestions(null);
    };

    const fetchSearch = (body, signal) =>
        fetchJSON({
            url: api,
            method: 'POST',
            body: JSON.stringify(body),
            signal
        });

    const getSuggestions = async (query, category) => {
        setIsLoading(true);

        const isValid = isSearchValueValid({
            value: query,
            maxLength,
            minLength
        });

        if (!isValid) {
            clearSuggestions();
            return;
        }

        // cancel previous request
        if (searchRequestAbortController) {
            searchRequestAbortController.abort();
        }

        // make request cancellable
        searchRequestAbortController = new AbortController();
        const { signal } = searchRequestAbortController;

        try {
            const response = await fetchSearch(
                {
                    [HEADER_SEARCH_QUERY_PARAMETER_ID]: query,
                    [HEADER_SEARCH_CATEGORY_PARAMETER_ID]: category,
                    siteName,
                    culture
                },
                signal
            );

            handleSearchSuccess(response);
        } catch (error) {
            handleSearchError(error);
        } finally {
            setIsLoading(false);
        }
    };

    const getDebouncedSuggestions = useCallback(debounce(getSuggestions, 300), [
        menuIsOpen
    ]);

    const handleChange = event => {
        const { target } = event;

        setSearchValue(target.value);

        if (isSearchValueValid({ maxLength, minLength, value: target.value })) {
            getDebouncedSuggestions(target.value, categoryValue);
        } else {
            setSuggestions(null);

            if (menuIsNotEmpty) {
                setMenuIsOpen(true);
            }
        }
    };

    const handleSelectChange = option => {
        const { value } = option;
        setCategoryValue(value);

        if (isSearchValueValid({ maxLength, minLength, value: searchValue })) {
            getDebouncedSuggestions(searchValue, value);
        }
    };

    const goToSerp = () => {
        window.location.href = composedSerpUrl;
    };

    const handleSubmit = event => {
        event.preventDefault();

        // prevent submitting empty form
        if (!searchValue || !searchValue.trim()) {
            return;
        }

        goToSerp();
    };

    const handleFocus = () => {
        setIsFocused(true);

        if (searchValue.length < minLength && menuIsNotEmpty) {
            setMenuIsOpen(true);
        }
    };

    const handleBlur = () => {
        setIsFocused(false);
    };

    const handleCloseButtonClick = () => {
        clearSuggestions();

        setMenuIsOpen(false);
    };

    const toggleBodySearchMenuIsOpenClassName = () => {
        if (!bodyRef.current) {
            return;
        }

        if (menuIsOpen || suggestions) {
            bodyRef.current.classList.add(classnames.SEARCH_DROPDOWN_IS_OPEN);
        } else {
            bodyRef.current.classList.remove(
                classnames.SEARCH_DROPDOWN_IS_OPEN
            );
        }
    };

    const toggleBodySearchIsOpenClassName = () => {
        if (!bodyRef.current) {
            return;
        }

        if (mobileSearchIsOpen) {
            bodyRef.current.classList.add(classnames.SEARCH_IS_OPEN);
        } else {
            bodyRef.current.classList.remove(classnames.SEARCH_IS_OPEN);
        }
    };

    // disable/enable page scroll
    const togglePageScroll = () => {
        if (menuIsOpen || suggestions || mobileSearchIsOpen) {
            scroll.forbid();
        } else {
            scroll.restore();
        }
    };

    const handleSearchButtonClick = () => {
        setMobileSearchIsOpen(true);
        if (!inputRef.current) {
            return;
        }

        inputRef.current.focus();
    };

    const handleCloseSearchButtonClick = () => {
        clearSuggestions();
        setMobileSearchIsOpen(false);
        setMenuIsOpen(false);
    };

    const handleClickOutside = () => {
        clearSuggestions();

        setMenuIsOpen(false);
    };

    const handleDidMount = () => {
        bodyRef.current = document.body;
    };

    useEffect(() => handleDidMount(), []);

    useEffect(() => {
        toggleBodySearchIsOpenClassName();
        togglePageScroll();
    }, [mobileSearchIsOpen]);

    useEffect(() => {
        toggleBodySearchMenuIsOpenClassName();
        togglePageScroll();
    }, [menuIsOpen, suggestions]);

    return {
        handleFocus,
        handleBlur,
        handleChange,
        handleSelectChange,
        handleSubmit,
        handleCloseButtonClick,
        handleSearchButtonClick,
        handleCloseSearchButtonClick,
        handleClickOutside,
        isFocused,
        categoryValue,
        searchValue,
        suggestions: shouldShowSuggestions ? suggestions : null,
        composedSerpUrl,
        menuIsOpen,
        inputRef,
        resultsAmount,
        isLoading
    };
};

export default useSearch;
